import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type { Cart } from '@pylot-data/pylotschema'
import { removeItemFromCartMutation as removeItemFromCartMutationCheckout } from '@corratech/pylot-cart-manager/src/graphql/removeItemFromCartMutation'
import { removeItemFromCartMutation } from '@components/alfakher/pylot-cart-manager/src/graphql/removeItemFromCartMutation'
import { CartData, useCart } from '@corratech/pylot-cart-manager/src/use-cart'
import { getIsSignedIn } from '@corratech/pylot-cart-manager/src/utils/getIsSignedIn'
import { useUI } from '@corratech/pylot-ui/context'

export type RemoveItemFromCartInput = {
    input: {
        cart_id: string
        cart_item_uid: string
    }
    isSignedIn: boolean
}

export type RemoveItemFromCartOutput = {
    removeItemFromCart: {
        cart: Cart
    }
}

export type RemoveItemFromCart = (cartItemUid: string) => Promise<void>
export interface UseRemoveItemFromCartReturn {
    removeItemFromCart: RemoveItemFromCart
}

const formatData = (
    output: GraphqlFetchOutput<RemoveItemFromCartOutput>
): CartData => {
    return { data: { cart: output?.data?.removeItemFromCart?.cart } }
}

const getOptimisticData = (
    cartData: CartData,
    cartItemUid: string
): CartData => {
    const items = [...(cartData.data.cart.items || [])]
    const itemIndex = items.findIndex((item) => item?.uid === cartItemUid)
    items.splice(itemIndex, 1)

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items
            },
            isOptimistic: true
        }
    }
}

export const useRemoveItemFromCart = (): UseRemoveItemFromCartReturn => {
    const { locale, route } = useRouter()
    const { data, mutate } = useCart()
    const { setMinicartLoading } = useUI()

    return {
        removeItemFromCart: useCallback(
            async (cartItemUid: string) => {
                const optimisticData = getOptimisticData(data!, cartItemUid)
                mutate(optimisticData, false)
                setMinicartLoading(true)
                const response = await mutate(
                    graphqlFetch<
                        RemoveItemFromCartInput,
                        RemoveItemFromCartOutput
                    >({
                        query:
                            route === '/checkout'
                                ? removeItemFromCartMutationCheckout
                                : removeItemFromCartMutation,
                        variables: {
                            input: {
                                cart_id: String(data!.data.cart.id!),
                                cart_item_uid: cartItemUid
                            },
                            isSignedIn: getIsSignedIn()
                        },
                        locale
                    }).then((res) => formatData(res)),
                    false
                )
                setMinicartLoading(false)
                mutate(response, false)
            },
            // TODO: check if data from hook changes every time
            [data, mutate]
        )
    }
}
